import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { HttpService } from '../http/http.service';
import { localStorageVar } from 'src/app/modules/admin/pages/localstoragevar';
import { Router } from '@angular/router';
import { signUp } from '../../interface/auth/auth';
import { BusinessSettings, defaultSettings } from '../../interface';
import { Distribution } from '../../interface/distributions/distributions';
import { LoginResponse } from '../../interface/auth/login-response.model';
import { setPermissions } from 'src/app/utils';
import * as Sentry from "@sentry/angular";
import { ProductService } from '../product/product.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _user: BehaviorSubject<any | null>;
  private _franchises: BehaviorSubject<Distribution[] | null>;
  private _activeFranchise: BehaviorSubject<Distribution | null>;

  public token: BehaviorSubject<string | null>;
  public accountLevel: BehaviorSubject<any | null>
  public _businessSettings: BehaviorSubject<BusinessSettings | null>
  private _distributionSettings: BehaviorSubject<BusinessSettings | null>
  private _permissionsChanged$ = new Subject<void>();

  public get user() { return this._user.value; }
  public get user$() { return this._user.asObservable(); }
  public get businessSettings() { return this._businessSettings.value; }
  public get businessSettings$() { return this._businessSettings.asObservable(); }
  public get distributionSettings() { return this._distributionSettings.value; }
  public get distributionSettings$() { return this._distributionSettings.asObservable(); }
  public get franchises() { return this._franchises.value; }
  public get franchises$() { return this._franchises.asObservable(); }
  public get activeFranchise() { return this._activeFranchise.value; }
  public get activeFranchise$() { return this._activeFranchise.asObservable(); }
  public get permissionsChanged$() { return this._permissionsChanged$.asObservable(); }

  public get userRole() { return this._user.value?.role; }
  public get getUserRole() { return this._user.value?.role?.toLocaleLowerCase(); }

  constructor(private _service: HttpService, private router: Router, private _productService: ProductService) {
    this.token = new BehaviorSubject<string | null>(null); // Token lives in httpOnly cookie, not localStorage
    this._user = new BehaviorSubject(JSON.parse(localStorage.getItem(localStorageVar.loginInfo)) || null);
    this.accountLevel = new BehaviorSubject(localStorage.getItem(localStorageVar.accountLevel || null));
    this._businessSettings = new BehaviorSubject(JSON.parse(localStorage.getItem(localStorageVar.businessSettings || null)));
    this._distributionSettings = new BehaviorSubject(this._enrichSubscription(JSON.parse(localStorage.getItem(localStorageVar.distributionSettings) || null)));
    this._franchises = new BehaviorSubject(JSON.parse(localStorage.getItem(localStorageVar.franchises || null)));
    this._activeFranchise = new BehaviorSubject(JSON.parse(localStorage.getItem(localStorageVar.activeFranchise || null)));
    // Permissions are loaded by APP_INITIALIZER after DI is fully wired (see app.module.ts)

    this.user$.subscribe((x) => {
      if(x){
        Sentry.setUser({
          id: String(x.id ?? ''),
          role: x.role,
          accountLevel: x.accountLevel,
        });
      }
    });
    this.activeFranchise$.subscribe((x:any) => {

      if(x){
        Sentry.setContext("franchise", {
          id:x.id,
          name:x.name,
          settingsId:x.settingsId,
        });
      }
    });

  }

  public RefreshFranchises(){
    var franchises = JSON.parse(localStorage.getItem(localStorageVar.franchises || null));
    this._franchises.next([...franchises]);
    var activeFranchise = JSON.parse(localStorage.getItem(localStorageVar.activeFranchise || null));
    this._activeFranchise.next({...activeFranchise});
  }

  public updateBusiness(val: any) {
    let settings= this._businessSettings.value;
    settings={...settings,...val};
    localStorage.setItem(localStorageVar.businessSettings, JSON.stringify(settings));
    this._businessSettings.next(settings);
  }

  public updateUserProfile(patch: { firstName?: string; lastName?: string }): void {
    const updated = { ...this._user.value, ...patch };
    this._user.next(updated);
    localStorage.setItem(localStorageVar.loginInfo, JSON.stringify(updated));
  }

  public async updateProfile(data: { firstName: string; lastName?: string; phone?: string; address?: string }): Promise<any> {
    const result = await firstValueFrom(this._service.patch('/auth/', 'profile', data));
    this.updateUserProfile({ firstName: data.firstName, lastName: data.lastName });
    return result;
  }
  async requestForAuth(email: string, Password: string): Promise<LoginResponse> {
    var prom = await firstValueFrom(this._service.post('/auth', { email, Password }));
    this._storeTokens(prom);
    await this._getSettings(prom.settings);
    return prom;
  }

  private _storeTokens(prom: LoginResponse): LoginResponse {
    const franchises = (prom?.franchises ?? [] as any).map(x => ({
      name: x.name,
      id: x.id,
      settingsId: x.settingsId,
      distributionId: x.distributionId
    }));
    let activeFranchise: any = prom?.activeFranchise ?? {};
    activeFranchise.distribution = undefined;

    this.token.next(prom?.token);

    // Only store non-sensitive profile data — strip token, PII, and permissions
    const profile = {
      id: (prom as any)?.id,
      firstName: (prom as any)?.firstName,
      lastName: (prom as any)?.lastName,
      role: (prom as any)?.role,
      accountLevel: (prom as any)?.accountLevel,
    };
    const distributionSettings = this._enrichSubscription(prom?.distributionSettings ?? null);
    
    localStorage.setItem(localStorageVar.loginInfo, JSON.stringify(profile));
    localStorage.setItem(localStorageVar.accountLevel, (prom as any)?.accountLevel);
    localStorage.setItem(localStorageVar.businessSettings, JSON.stringify(activeFranchise.settings));
    localStorage.setItem(localStorageVar.distributionSettings, JSON.stringify(distributionSettings));
    localStorage.setItem(localStorageVar.franchises, JSON.stringify(franchises));
    localStorage.setItem(localStorageVar.activeFranchise, JSON.stringify(activeFranchise));
    // Permissions are kept in memory only — never persisted to localStorage
    setPermissions((prom as any)?.permissions);
    this._user.next(profile);
    this._franchises.next(franchises);
    this._businessSettings.next(activeFranchise.settings);
    this._distributionSettings.next(distributionSettings);
    this._activeFranchise.next(activeFranchise);
    // Deferred so the sidebar menu rebuilds after routing has settled
    setTimeout(() => {
      this._permissionsChanged$.next();
    }, 1000);
    return prom;
  }

  public async signup(data: signUp): Promise<LoginResponse> {
    var prom = await firstValueFrom(this._service.post('/Auth/SignUp', data));
    this._storeTokens(prom);
    await this._getSettings(prom.settings);
    return prom;
  }
  public forget(email: any) {
    return this._service.post('/auth/forgotPassword', email);

  }
  public reset(data: any) {
    return this._service.post('/auth/resetPassword', data);
  }
  public change(data: any) {
    return this._service.post('/auth/changePassword', data)
  }
  private async _getSettings(settings:any=null):Promise<void> {
    const response = await firstValueFrom(this._service.get('/settings'));
    if (!response)return;
     
      const mergedSettings = this._mergeSettings(response,settings);
      this.updateBusiness(mergedSettings);
    
  }
  private _mergeSettings(responseSettings: any[], settings:any): { [key: string]: string } {
    const settingsMap = responseSettings.reduce((acc, setting) => {
      acc[setting.key] = setting.value;
      return acc;
    }, {} as { [key: string]: string });
    return { ...settings, ...settingsMap };
  }


  public async updateSettings(key: string, value: string): Promise<Distribution> {
    return (await firstValueFrom(this._service.patch('/settings','', { key, value,type:'franchise' })).then(x => {
      this._getSettings(this._businessSettings.value);
      return x;
    }));
  }

  public async loadPermissions(): Promise<void> {
    if (!this._user.value) return;
    try {
      const perms: string[] = await firstValueFrom(this._service.get('/auth/session'));
      setPermissions(perms);
      this._permissionsChanged$.next();
    } catch (e: any) {
      if (e?.status === 401 || e?.status === 403) {
        this.logout();
      }
    }
  }

  public logout() {
    // Revoke refresh token + clear cookies server-side
    this._service.post('/auth/logout', {}).subscribe({ error: () => {} });
    localStorage.removeItem(localStorageVar.loginInfo);
    localStorage.removeItem(localStorageVar.businessSettings);
    localStorage.removeItem(localStorageVar.distributionSettings);
    localStorage.removeItem(localStorageVar.accountLevel);
    localStorage.removeItem(localStorageVar.franchises);
    localStorage.removeItem(localStorageVar.activeFranchise);
    setPermissions([]); // Clear in-memory permissions
    this._permissionsChanged$.next();
    if (this.token.value)
      this.token.next(null);
    if (this._user.value)
      this._user.next(null);
    if (this._businessSettings.value)
      this._businessSettings.next(null);
    if (this._distributionSettings.value)
      this._distributionSettings.next(null);
    if (this._activeFranchise.value)
      this._activeFranchise.next(null);
    if (this._franchises.value)
      this._franchises.next(null);
    this.router.navigateByUrl('/browse');
  }

  private _enrichSubscription(settings: BusinessSettings | null): BusinessSettings | null {
    if (!settings) return null;
    const expiresAt = settings.subscriptionExpiresAt;
    if (!expiresAt) {
      return { ...settings, subscriptionStatus: 'unlimited', daysRemaining: null };
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const expiry = new Date(expiresAt);
    expiry.setHours(0, 0, 0, 0);
    const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    let subscriptionStatus: BusinessSettings['subscriptionStatus'];
    if (daysRemaining <= 0) {
      subscriptionStatus = 'expired';
    } else if (daysRemaining <= (settings.notifyBeforeDays ?? 0)) {
      subscriptionStatus = 'warning';
    } else {
      subscriptionStatus = 'active';
    }
    return { ...settings, daysRemaining, subscriptionStatus };
  }

  public async refreshToken(): Promise<string | null> {
    try {
      const res = await firstValueFrom(this._service.post('/auth/refresh', {}));
      if (!res?.token) return null;
      this.token.next(res.token);
      if (res.distributionSettings) {
        const distributionSettings = this._enrichSubscription(res.distributionSettings);
        this._distributionSettings.next(distributionSettings);
        localStorage.setItem(localStorageVar.distributionSettings, JSON.stringify(distributionSettings));
      }
      return res.token;
    } catch (error: any) {
      // Propagate auth errors (refresh token invalid/expired) so the interceptor can logout.
      // Swallow transient errors (network, 5xx) — don't logout on a temporary blip.
      if (error?.status === 401 || error?.status === 403) throw error;
      return null;
    }
  }

  public async switchFranchise(franchiseId: number): Promise<LoginResponse> {
    const x: LoginResponse = await firstValueFrom(this._service.post('/auth/switch/' + franchiseId, {}));
    if (!x) return x;
    this._productService.clearProductCache();
    // update the franchises and active franchise
    this.token.next(x.token);
    this._activeFranchise.next(x.activeFranchise);
    this._franchises.next(x.franchises);
    this._businessSettings.next(x.settings);
    if (x.distributionSettings) {
      const distributionSettings = this._enrichSubscription(x.distributionSettings);
      this._distributionSettings.next(distributionSettings);
      localStorage.setItem(localStorageVar.distributionSettings, JSON.stringify(distributionSettings));
    }
    // update the local storage (token stays in httpOnly cookie)
    localStorage.setItem(localStorageVar.businessSettings, JSON.stringify(x?.settings));
    localStorage.setItem(localStorageVar.franchises, JSON.stringify(x?.franchises));
    localStorage.setItem(localStorageVar.activeFranchise, JSON.stringify(x?.activeFranchise));
    setPermissions((x as any)?.permissions ?? []);
    setTimeout(() => {
    this._permissionsChanged$.next();
    }, 1000);
    await this._getSettings(x.settings);
    return x;
  }
  public async getPermissions():Promise<any> {
    return firstValueFrom(this._service.get('/permissions'));
  }
}
